import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import Select from "@/components/Select";
import useFilters from "@/lib/useFilters";
import FilterActions from "@/components/FilterActions";
import { useAuth } from "@/context/AuthContext";
import { lock } from "@/lib/perm";

const MONTHS = ["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie","Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"];

export default function CalendarView() {
  const { has } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [items, setItems] = useState([]);
  const [emps, setEmps] = useState([]);
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [holidayCal, setHolidayCal] = useState(null);
  // filtrul de structură se aplică abia la „Aplică filtre”; implicit se afișează toți
  const cf = useFilters({ unit: "" });
  const unitFilter = cf.applied.unit;

  useEffect(() => {
    Promise.all([
      api.get(`/planco/calendar/${year}`), api.get("/mdm/employments"),
      (has("users.list") ? api.get("/users") : Promise.resolve({data:[]})).catch(()=>({data:[]})), api.get("/mdm/org-units"),
      api.get(`/mdm/holidays/${year}`).catch(()=>({data:null}))
    ]).then(([c,e,u,o,h]) => {
      setItems(c.data); setEmps(e.data); setUsers(u.data); setUnits(o.data); setHolidayCal(h.data);
    });
  }, [year]);

  const holidaySet = useMemo(() => new Set((holidayCal?.days || []).map(d => d.date)), [holidayCal]);
  const filteredEmps = useMemo(() => emps.filter(e => e.status !== "terminated" && (!unitFilter || e.org_unit_id === unitFilter)), [emps, unitFilter]);

  const daysInYear = useMemo(() => {
    const out = [];
    for (let m = 0; m < 12; m++) {
      const dim = new Date(year, m+1, 0).getDate();
      for (let d = 1; d <= dim; d++) out.push({ m, d, dow: new Date(year,m,d).getDay() });
    }
    return out;
  }, [year]);

  const cellStatus = (empId, m, d) => {
    const iso = `${year}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
    for (const it of items) {
      if (it.employment_id !== empId) continue;
      if (iso >= it.start_date && iso <= it.end_date) return it.status;
    }
    return null;
  };

  return (
    <>
      <PageHeader title="Calendar Programări" subtitle={`Vizualizare heatmap ${year}`} actions={<>
        <Select className="select" style={{width:100}} value={year} onChange={e=>setYear(parseInt(e.target.value))} data-testid="cal-year">
          {[year-1,year,year+1].map(y=><option key={y}>{y}</option>)}
        </Select>
        <form style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}} onSubmit={e=>{e.preventDefault(); cf.apply();}}>
          <Select className="select" style={{width:200}} value={cf.draft.unit} onChange={e=>cf.set({unit:e.target.value})} data-testid="cal-unit">
            <option value="">Toate structurile</option>
            {units.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
          <FilterActions f={cf} testid="cal-filters" />
        </form>
      </>} />

      <div style={{display:"flex",gap:14,marginBottom:14,fontSize:11,color:"#334155",flexWrap:"wrap"}}>
        <Legend cls="approved" label="Aprobat" />
        <Legend cls="submitted" label="În așteptare" />
        <Legend cls="draft" label="Ciornă" />
        <Legend cls="rejected" label="Respins" />
        <Legend cls="holiday" label="Sărbătoare" />
        <Legend cls="wknd" label="Weekend" />
      </div>

      <div className="card" style={{overflow:"auto"}}>
        <table style={{borderCollapse:"collapse",fontSize:10}}>
          <thead>
            <tr>
              <th style={{position:"sticky",left:0,background:"#F8FAFC",padding:"6px 10px",borderBottom:"1px solid #E2E8F0",textAlign:"left",minWidth:220,zIndex:2,fontSize:11,color:"var(--navy)",fontWeight:700}}>Angajat / Marcă</th>
              {MONTHS.map((mn,mi)=>{
                const dim = new Date(year,mi+1,0).getDate();
                return <th key={mi} colSpan={dim} style={{padding:6,borderBottom:"1px solid #E2E8F0",background:"#F8FAFC",fontWeight:700,color:"var(--navy)"}}>{mn}</th>;
              })}
            </tr>
            <tr>
              <th style={{position:"sticky",left:0,background:"#F8FAFC",zIndex:2}}></th>
              {daysInYear.map((x,i)=>(
                <th key={i} style={{padding:1,background:"#F8FAFC",borderBottom:"1px solid #E2E8F0",fontWeight:400,color:"#94A3B8",minWidth:14}}>{x.d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredEmps.map(emp => {
              const u = users.find(x=>x.id===emp.user_id);
              return (
                <tr key={emp.id} data-testid={`cal-row-${emp.marca}`}>
                  <td style={{position:"sticky",left:0,background:"white",padding:"5px 10px",borderBottom:"1px solid #F1F5F9",minWidth:220,fontSize:11,zIndex:1}}>
                    <div style={{fontWeight:600,color:"var(--navy)"}}>{u ? `${u.last_name} ${u.first_name}` : (emp.user_name || "—")}</div>
                    <div style={{color:"#64748B",fontSize:10}} className="tabular-nums">{emp.marca}</div>
                  </td>
                  {daysInYear.map((x,i)=>{
                    const iso = `${year}-${String(x.m+1).padStart(2,"0")}-${String(x.d).padStart(2,"0")}`;
                    const isHol = holidaySet.has(iso);
                    const st = cellStatus(emp.id, x.m, x.d);
                    const wknd = x.dow===0 || x.dow===6;
                    const cls = st || (isHol ? "holiday" : (wknd ? "wknd" : ""));
                    return <td key={i} className={`cal-cell ${cls}`} title={isHol ? "Sărbătoare" : (st || "")} style={{padding:0,minWidth:14}}></td>;
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredEmps.length===0 && <div style={{padding:40,textAlign:"center",color:"#94A3B8"}}>Niciun raport activ.</div>}
      </div>
    </>
  );
}
function Legend({ cls, label }) {
  return <div style={{display:"flex",alignItems:"center",gap:6}}>
    <span className={`cal-cell ${cls}`} style={{width:16,height:16,display:"inline-block"}} />
    <span>{label}</span>
  </div>;
}
